import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// Build a displayable URL for a logo that may be a relative path (/uploads/...) or full URL
const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
const resolveLogoUrl = (logo) => {
  if (!logo) return '';
  if (logo.startsWith('http')) return logo;
  return `${API_HOST}${logo}`;
};

const isValidHex = (hex) => /^#[0-9A-Fa-f]{6}$/.test(hex || '');

// Same luminance formula as backend/src/services/pdfService.js's
// readableTextColor() — kept in sync manually (no shared JS package between
// the two apps) so this preview matches what the PDF actually renders.
const readableTextColor = (hex) => {
  if (!isValidHex(hex)) return '#ffffff';
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? '#111827' : '#ffffff';
};

const CompanyProfilePage = () => {
  const { user, refreshUser, updateCompanyLogo } = useAuth();
  const [company, setCompany]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState('');
  const [error, setError]       = useState('');
  const [form, setForm]         = useState({ name: '', phone: '', address: '', whatsapp: '', logo: '', brandColor: '#1a3a6e', code: '', bankAccounts: [] });
  const [newBank, setNewBank]   = useState({ bankName: '', accountNumber: '', accountHolder: '' });
  const [addingBank, setAddingBank]   = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError]         = useState('');
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (!user?.companyId) return;
    api.get(`/companies/${user.companyId}`)
      .then((r) => {
        const c = r.data.data;
        setCompany(c);
        setForm({ name: c.name || '', phone: c.phone || '', address: c.address || '', whatsapp: c.whatsapp || '', logo: c.logo || '', brandColor: c.brandColor || '#1a3a6e', code: c.code || '', bankAccounts: c.bankAccounts || [] });
      })
      .catch(() => setError('فشل تحميل بيانات الشركة'))
      .finally(() => setLoading(false));
  }, [user]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const setBank = (f) => (e) => setNewBank((p) => ({ ...p, [f]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    if (form.brandColor && !isValidHex(form.brandColor)) {
      setError('لون غير صالح — يجب أن يكون بصيغة # متبوعة بستة أحرف (مثال: #1a3a6e)');
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put(`/companies/${user.companyId}`, form);
      setSuccess('تم حفظ بيانات الشركة بنجاح');
    } catch (e) {
      setError(e.response?.data?.message || 'خطأ في الحفظ');
    } finally { setSaving(false); }
  };

  const addBank = () => {
    if (!newBank.bankName || !newBank.accountNumber || !newBank.accountHolder) return;
    setForm((f) => ({ ...f, bankAccounts: [...f.bankAccounts, newBank] }));
    setNewBank({ bankName: '', accountNumber: '', accountHolder: '' });
    setAddingBank(false);
  };

  const removeBank = (i) => {
    setForm((f) => ({ ...f, bankAccounts: f.bankAccounts.filter((_, idx) => idx !== i) }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setLogoError('');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post(`/companies/${user.companyId}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const logoUrl = res.data.logoUrl;
      setForm((p) => ({ ...p, logo: logoUrl }));
      updateCompanyLogo(logoUrl);
      await refreshUser();
    } catch (err) {
      setLogoError(err.response?.data?.message || 'فشل رفع الشعار');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-blue-600">جاري التحميل...</div>;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-3xl" dir="rtl">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">الملف الشركة</h1>
        <p className="text-gray-500 text-sm">إدارة بيانات شركتك والحسابات البنكية</p>
      </div>

      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">{success}</div>}
      {error   && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 text-sm">{error}</div>}

      {/* Status badge */}
      {company && (
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${company.isActive ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {company.isActive ? '✅ الشركة نشطة ومفعّلة' : '⏳ الشركة قيد مراجعة المدير العام'}
        </div>
      )}

      <form onSubmit={saveProfile} className="bg-white rounded-xl shadow p-6 space-y-5">
        <h2 className="font-bold text-gray-700 border-b pb-2">المعلومات الأساسية</h2>

        {/* Email (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">البريد الإلكتروني (لا يمكن تغييره)</label>
          <input type="text" value={company?.email || ''} disabled
            className="w-full border border-gray-200 bg-gray-50 rounded-lg px-4 py-2.5 text-sm text-gray-400" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">اسم الشركة *</label>
            <input type="text" required value={form.name} onChange={set('name')}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              رمز الشركة
              <span className="text-gray-400 font-normal mr-1">(3 أحرف إنجليزية)</span>
            </label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().substring(0, 3) }))}
              placeholder="SAF"
              maxLength={3}
              dir="ltr"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-mono tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">يظهر في بداية رقم التذكرة — مثال: <span className="font-mono text-blue-600">SAF</span>-PZU-KRT-...</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
            <input type="tel" value={form.phone} onChange={set('phone')}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">العنوان / المدينة</label>
            <input type="text" value={form.address} onChange={set('address')}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">رقم الواتساب</label>
            <input type="tel" placeholder="+249912345678" value={form.whatsapp} onChange={set('whatsapp')}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">لون الشركة (لون التذكرة)</label>
            <div className="flex items-center gap-3">
              <input type="color" value={isValidHex(form.brandColor) ? form.brandColor : '#1a3a6e'} onChange={set('brandColor')}
                className="h-10 w-16 rounded border border-gray-300 cursor-pointer p-1" />
              <input type="text" value={form.brandColor} onChange={set('brandColor')}
                placeholder="#1a3a6e" maxLength={7}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {/* Live preview — the ticket PDF auto-selects black/white header
                  text against this exact color (pdfService.js's readableTextColor),
                  shown here so the company sees the same result before saving. */}
              {isValidHex(form.brandColor) && (
                <div className="h-10 px-4 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: form.brandColor, color: readableTextColor(form.brandColor) }}>
                  تذكرة سفر
                </div>
              )}
            </div>
            {form.brandColor && !isValidHex(form.brandColor) && (
              <p className="text-xs text-red-500 mt-1">صيغة غير صالحة — يجب أن تكون # متبوعة بستة أحرف (مثال: #1a3a6e)</p>
            )}
            <p className="text-xs text-gray-400 mt-1">هذا اللون يُستخدم لرأس التذكرة. أدخل رمز Hex مثل #1a3a6e</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">شعار الشركة (Logo)</label>
            <div className="flex items-center gap-4">
              {/* Logo preview */}
              <div className="flex-shrink-0 h-20 w-20 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                {form.logo ? (
                  <img src={resolveLogoUrl(form.logo)} alt="شعار الشركة" className="h-full w-full object-contain p-1"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <span className="text-3xl text-gray-300">🏢</span>
                )}
              </div>
              {/* Upload controls */}
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  ref={logoInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-file-input"
                />
                <label
                  htmlFor="logo-file-input"
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition ${
                    logoUploading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  onClick={(e) => logoUploading && e.preventDefault()}
                >
                  {logoUploading ? (
                    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> جاري الرفع...</>
                  ) : (
                    <><span>📤</span> {form.logo ? 'تغيير الشعار' : 'رفع الشعار'}</>
                  )}
                </label>
                {form.logo && !logoUploading && (
                  <button type="button" onClick={() => setForm((p) => ({ ...p, logo: '' }))}
                    className="block text-xs text-red-500 hover:underline">
                    حذف الشعار
                  </button>
                )}
                <p className="text-xs text-gray-400">JPG، PNG، WebP — حجم أقصى 2MB. يظهر في رأس التذكرة.</p>
                {logoError && <p className="text-xs text-red-500">{logoError}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Bank Accounts */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-700">الحسابات البنكية</h2>
            <button type="button" onClick={() => setAddingBank(!addingBank)}
              className="text-sm text-blue-600 hover:underline">
              {addingBank ? 'إلغاء' : '+ إضافة حساب'}
            </button>
          </div>

          {addingBank && (
            <div className="bg-blue-50 rounded-lg p-4 mb-3 space-y-3">
              {[['bankName', 'اسم البنك', 'بنك الراجحي'], ['accountNumber', 'رقم الحساب', 'SA00...'], ['accountHolder', 'اسم صاحب الحساب', 'محمد أحمد']].map(([f, label, ph]) => (
                <div key={f}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <input type="text" placeholder={ph} value={newBank[f]} onChange={setBank(f)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}
              <button type="button" onClick={addBank}
                className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800">
                إضافة الحساب
              </button>
            </div>
          )}

          <div className="space-y-2">
            {form.bankAccounts.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4 border border-dashed rounded-lg">
                لم تُضف حسابات بنكية بعد. أضف حساباً ليتمكن المسافرون من التحويل.
              </p>
            ) : form.bankAccounts.map((b, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border">
                <div>
                  <p className="font-medium text-gray-800">{b.bankName}</p>
                  <p className="text-gray-500 text-xs">{b.accountNumber} — {b.accountHolder}</p>
                </div>
                <button type="button" onClick={() => removeBank(i)}
                  className="text-red-500 hover:text-red-700 text-sm">حذف</button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={saving}
            className="bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white px-8 py-2.5 rounded-lg font-semibold transition">
            {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyProfilePage;
